import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ViolationDetectionPanel = ({ violations, scanResults, loading, onGenerateRemediation }) => {
  const [selectedViolations, setSelectedViolations] = useState([]);
  const [generatingRemediation, setGeneratingRemediation] = useState(false);

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'text-error',
      high: 'text-warning',
      medium: 'text-primary',
      low: 'text-success'
    };
    return colors?.[severity] || 'text-muted-foreground';
  };

  const getSeverityBg = (severity) => {
    const backgrounds = {
      critical: 'bg-error/10',
      high: 'bg-warning/10',
      medium: 'bg-primary/10',
      low: 'bg-success/10'
    };
    return backgrounds?.[severity] || 'bg-muted';
  };

  const handleSelectViolation = (violationId) => {
    setSelectedViolations(prev => 
      prev?.includes(violationId) 
        ? prev?.filter(id => id !== violationId)
        : [...prev, violationId]
    );
  };

  const handleGenerateRemediation = async () => {
    if (selectedViolations?.length === 0) return;
    
    try {
      setGeneratingRemediation(true);
      await onGenerateRemediation(selectedViolations);
      setSelectedViolations([]);
    } catch (error) {
      console.error('Failed to generate remediation:', error);
    } finally {
      setGeneratingRemediation(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin mb-4" />
        <p className="text-sm text-muted-foreground">Scanning for violations...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* AI-Generated Alerts */}
      {scanResults?.criticalViolations && scanResults?.criticalViolations?.length > 0 && (
        <div className="bg-error/5 border border-error/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="AlertTriangle" size={20} color="var(--color-error)" />
            <h4 className="font-heading font-semibold text-base text-foreground">
              AI-Detected Critical Issues
            </h4>
          </div>
          <div className="space-y-2">
            {scanResults?.criticalViolations?.map((violation, index) => (
              <div key={index} className="bg-card rounded-lg p-3">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="text-sm font-medium text-foreground">{violation?.type}</p>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    getSeverityBg(violation?.severity)
                  } ${getSeverityColor(violation?.severity)}`}>
                    {violation?.severity?.toUpperCase()}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mb-2">{violation?.description}</p>
                {violation?.affectedCount && (
                  <p className="text-xs text-warning">
                    Affects {violation?.affectedCount} employees
                  </p>
                )}
                {violation?.deadline && (
                  <p className="text-xs text-error mt-1">
                    Deadline: {violation?.deadline}
                  </p>
                )}
                {violation?.actionRequired && (
                  <div className="mt-2 p-2 bg-muted/50 rounded">
                    <p className="text-xs font-medium text-foreground mb-1">Action Required:</p>
                    <p className="text-xs text-muted-foreground">{violation?.actionRequired}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Violation Feed */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-heading font-semibold text-base text-foreground">
            Detected Violations ({violations?.length})
          </h4>
          {selectedViolations?.length > 0 && (
            <Button
              variant="primary"
              size="sm"
              onClick={handleGenerateRemediation}
              disabled={generatingRemediation}
              iconName={generatingRemediation ? 'Loader2' : 'Sparkles'}
            >
              {generatingRemediation ? 'Generating...' : `Generate Remediation (${selectedViolations?.length})`}
            </Button>
          )}
        </div>

        {violations?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="CheckCircle2" size={48} color="var(--color-success)" className="mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No violations detected</p>
          </div>
        ) : (
          <div className="space-y-3">
            {violations?.map((violation) => (
              <div 
                key={violation?.id} 
                className={`border rounded-lg p-4 transition-all ${
                  selectedViolations?.includes(violation?.id) 
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedViolations?.includes(violation?.id)}
                    onChange={() => handleSelectViolation(violation?.id)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <h5 className="font-heading font-semibold text-sm text-foreground mb-1">
                          {violation?.violationType?.replace(/_/g, ' ')}
                        </h5>
                        <p className="text-xs text-muted-foreground">{violation?.description}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                        getSeverityBg(violation?.severity)
                      } ${getSeverityColor(violation?.severity)}`}>
                        {violation?.severity?.toUpperCase()}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Affected</p>
                        <p className="text-sm font-medium text-foreground">
                          {violation?.affectedCount} employees
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Department</p>
                        <p className="text-sm font-medium text-foreground">
                          {violation?.department || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Status</p>
                        <p className="text-sm font-medium text-foreground capitalize">
                          {violation?.violationStatus?.replace(/_/g, ' ')}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Identified</p>
                        <p className="text-sm font-medium text-foreground">
                          {new Date(violation.identifiedAt)?.toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    {violation?.regulationReference && (
                      <div className="mt-3 p-2 bg-muted/50 rounded">
                        <p className="text-xs text-muted-foreground">
                          <span className="font-medium">Regulation:</span> {violation?.regulationReference}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ViolationDetectionPanel;